// MazotListesi tasarım/mantığı: solda Alanlar + kolon filtreleri (arama dahil) + preset tarih + otomatik getir + Excel export
// Hazır Beton özel mantık:
// - 3 ayrı m3: Zone Üretim m3 (zone.satisM3), Mikser m3 (mikser.m3), Pompa m3 (pompa.m3) aynı irsaliyede yan yana
// - Kayıt Türü: Zone Raporu / Mikser Sevkiyat / Pompa Sevkiyat (varsa birleşik: "Zone Raporu + Mikser Sevkiyat + Pompa Sevkiyat")
// - Pompa operatör/yağcı çoklu isim desteği (operatorAdi*, yagciAdi*)
// - Mikser Fiili KM = donusKm - cikisKm, Fark KM = votoKm - Fiili KM
// - Saatler KARIŞMAZ:
//    * Pompa süreleri SADECE pompa.baslangicSaat / pompa.bitisSaat
//    * Mikser süreleri SADECE mikser.cikisSaat / mikser.donusSaat
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "dayjs/locale/tr";

dayjs.locale("tr");

const INOSEL_ID = "27517";

// Toplanacak sayısal kolonlar (Tam liste)
const AGGREGATE_COLS = [
  "uretimM3_zone", "m3_mikser", "m3_pompa", "m3", 
  "votoKm", "fiiliKm", "farkKm", "sure", "cikisKm", "donusKm"
];

// Başlığına tıklayınca gruplanabilecek kolonlar
const GROUPABLE_COLS = [
  "tesis", "musteriAdi", "santiyeAdi", "mikserPlaka", "pompaPlaka", "urunAdi", "kayitTuru"
];

/* ======================= HELPERS ======================= */
const norm = (v) => String(v ?? "").toUpperCase().replace(/\s+/g, " ").trim();
const normKey = (v) => String(v ?? "").trim().toUpperCase().replace(/[^0-9A-Z]/g, "");
const toNum = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const fmtTr2 = (v) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtTr0 = (v) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const minutesToHHMM = (mins) => {
  if (mins == null) return "";
  const m = Math.max(0, Math.round(mins));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const parseHHMM = (hhmm) => {
  const t = String(hhmm ?? "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const calcSureFromTimes = (bas, bit) => {
  const s = parseHHMM(bas);
  const e = parseHHMM(bit);
  if (s == null || e == null) return "";
  let d = e - s;
  if (d < 0) d += 24 * 60; // gece devri
  return minutesToHHMM(d);
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
};

const collectIndexedNames = (obj, prefix) => {
  if (!obj) return "";
  const out = [];
  for (let i = 1; i <= 12; i++) {
    const k = i === 1 ? prefix : `${prefix}${i}`;
    const v = String(obj[k] ?? "").trim();
    if (v) out.push(v);
  }
  // ayrıca prefix ile başlayan alanlar (operatorAdiX vs)
  Object.keys(obj).forEach((k) => {
    if (k.startsWith(prefix)) {
      const v = String(obj[k] ?? "").trim();
      if (v && !out.some((x) => norm(x) === norm(v))) out.push(v);
    }
  });
  return out.join(" / ");
};

const calcFiiliKm = (cikisKm, donusKm) => {
  const ck = toNum(cikisKm);
  const dk = toNum(donusKm);
  if (ck == null || dk == null) return null;
  return dk - ck;
};

const calcFarkKm = (votoKm, fiiliKm) => {
  const v = toNum(votoKm);
  const f = toNum(fiiliKm);
  if (v == null || f == null) return null;
  return v - f;
};

/* ======================= KOLONLAR ======================= */
const COLUMNS = {
  recordType: "Kayıt Türü",
  tarih: "Tarih",
  tesis: "Tesis",
  irsaliyeNo: "İrsaliye",
  musteriAdi: "Müşteri",
  santiyeAdi: "Şantiye",

  mikserPlaka: "Mikser Plaka",
  pompaPlaka: "Pompa Plaka",

  // Zone
  uretimM3_zone: "Zone Üretim m3",
  votoKm: "Voto KM",

  // Mikser
  m3_mikser: "Mikser m3",
  mikserCikisSaat: "Mikser Çıkış Saat",
  mikserDonusSaat: "Mikser Dönüş Saat",
  mikserSure: "Mikser Süre",
  soforAdi: "Şoför",
  cikisKm: "Çıkış KM",
  donusKm: "Dönüş KM",
  fiiliKm: "Fiili KM",
  farkKm: "Fark KM",

  // Pompa
  m3_pompa: "Pompa m3",
  pompaBasSaat: "Pompa Baş. Saat",
  pompaBitSaat: "Pompa Bit. Saat",
  pompaSure: "Pompa Süre",
  operator: "Operatör",
  yardimci: "Yardımcı",

  // Ortak
  mikserFirmasi: "Mikser Firması",
  pompaFirmasi: "Pompa Firması",
  teslimSekli: "Teslim Şekli",

  warn: "Uyarı",
};

const NUMERIC = [
  "uretimM3_zone",
  "m3_mikser",
  "m3_pompa",
  "cikisKm",
  "donusKm",
  "fiiliKm",
  "votoKm",
  "farkKm",
];

const DATE_PRESETS = [
  { key: "BU_AY", label: "Bu Ay" },
  { key: "SON_7", label: "Son 7 Gün" },
  { key: "GECEN_AY", label: "Geçen Ay" },
  { key: "BU_YIL", label: "Bu Yıl" },
  { key: "GECEN_YIL", label: "Geçen Yıl" },
];

export default function HazirBetonRapor() {
  // --- TASARIM SİSTEMİ ---
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [reportName, setReportName] = useState("");

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const q = query(collection(db, "reportDesigns"), where("module", "==", "hazir-beton"));
        const snap = await getDocs(q);
        const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDesigns(list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")));
      } catch (e) { console.error("Tasarımlar yüklenemedi:", e); }
    };
    fetchDesigns();
  }, []);

  const handleSaveDesign = async () => {
    if (!reportName.trim()) return alert("Lütfen tasarım adı girin!");
    try {
      const payload = {
        name: reportName.trim(),
        module: "hazir-beton",
        columns: visibleCols,
        datePreset: preset,
        startDate: startDate,
        endDate: endDate,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "reportDesigns"), payload);
      setDesigns(prev => [...prev, { id: docRef.id, ...payload }].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setReportName("");
      alert("Tasarım kaydedildi!");
    } catch (e) { console.error(e); alert("Hata oluştu!"); }
  };

  const applyDesign = (id) => {
    setSelectedDesignId(id);
    const d = designs.find(x => x.id === id);
    if (!d) return;
    if (d.columns) setVisibleCols(d.columns);
    if (d.datePreset && d.datePreset !== "OZEL") {
      applyPreset(d.datePreset);
    } else {
      if (d.startDate) setStartDate(d.startDate);
      if (d.endDate) setEndDate(d.endDate);
      setPreset("OZEL");
    }
  };
  const [rows, setRows] = useState([]);
  const firstLoadRef = useRef(true);
  const allCols = useMemo(() => Object.keys(COLUMNS), []);

  // Tasarım aynı kalsın: tüm kolonlar başlangıçta açık
  // const [visibleCols, setVisibleCols] = useState(allCols); //tüm sütunlar seçili geliyordu.
  const [visibleCols, setVisibleCols] = useState([]);
  const [groupBy, setGroupBy] = useState("");
  const safeVisibleCols = useMemo(() => visibleCols.filter((c) => !!COLUMNS[c]), [visibleCols]);

  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const filterRef = useRef(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0, width: 260 });

  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [preset, setPreset] = useState("BU_AY");
  const [loading, setLoading] = useState(false);

  // Firma kod->isim (istersen genişlet)
  const FIRM_NAME_MAP = useMemo(() => ({ [INOSEL_ID]: "İNOSEL" }), []);

  /* ======================= DIŞ TIKLAMA (FİLTRE KAPAT) ======================= */
  useEffect(() => {
    const onDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setOpenFilter(null);
        setFilterSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  /* ======================= TARİH PRESET ======================= */
  const applyPreset = (p) => {
    const n = dayjs();
    if (p === "BU_AY") {
      setStartDate(n.startOf("month").format("YYYY-MM-DD"));
      setEndDate(n.format("YYYY-MM-DD"));
    } else if (p === "SON_7") {
      setStartDate(n.subtract(6, "day").format("YYYY-MM-DD"));
      setEndDate(n.format("YYYY-MM-DD"));
    } else if (p === "GECEN_AY") {
      const m = n.subtract(1, "month");
      setStartDate(m.startOf("month").format("YYYY-MM-DD"));
      setEndDate(m.endOf("month").format("YYYY-MM-DD"));
    } else if (p === "BU_YIL") {
      setStartDate(n.startOf("year").format("YYYY-MM-DD"));
      setEndDate(n.format("YYYY-MM-DD"));
    } else if (p === "GECEN_YIL") {
      const y = n.subtract(1, "year");
      setStartDate(y.startOf("year").format("YYYY-MM-DD"));
      setEndDate(y.endOf("year").format("YYYY-MM-DD"));
    }
    setPreset(p);
  };

  /* ======================= VERİ TOPLAMA (3 koleksiyon) ======================= */
  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (endDate < startDate) return;

    setLoading(true);
    try {
      // Zone
      const qZone = query(
        collection(db, "hazirbeton_zone_uretim"),
        
        where("tarihStr", ">=", startDate),
        where("tarihStr", "<=", endDate)
      );
      const zoneSnap = await getDocs(qZone);
      const zoneListRaw = zoneSnap.docs.map((d) => ({ _docId: d.id, ...(d.data() || {}) }));
      const zoneList = zoneListRaw.filter((x) => x && x.deleted !== true);

      // Mikser
      const qMikser = query(
        collection(db, "hazirbeton_mikser_sevkiyat"),
        
        where("tarihStr", ">=", startDate),
        where("tarihStr", "<=", endDate)
      );
      const mikserSnap = await getDocs(qMikser);
      const mikserListRaw = mikserSnap.docs.map((d) => ({ _docId: d.id, ...(d.data() || {}) }));
      const mikserList = mikserListRaw.filter((x) => x && x.deleted !== true);

      // Pompa
      const qPompa = query(
        collection(db, "hazirbeton_pompa_hakedis"),
        
        where("tarihStr", ">=", startDate),
        where("tarihStr", "<=", endDate)
      );
      const pompaSnap = await getDocs(qPompa);
      const pompaListRaw = pompaSnap.docs.map((d) => ({ _docId: d.id, ...(d.data() || {}) }));
      const pompaList = pompaListRaw.filter((x) => x && x.deleted !== true);

      // Map by irsaliye
      const zoneByIrs = new Map();
      zoneList.forEach((z) => {
        const irs = normKey(z.irsaliyeNo);
        if (!irs) return;
        if (!zoneByIrs.has(irs)) zoneByIrs.set(irs, z);
      });

      const mikserByIrs = new Map();
      mikserList.forEach((m) => {
        const irs = normKey(m.irsaliyeNo);
        if (!irs) return;
        if (!mikserByIrs.has(irs)) mikserByIrs.set(irs, m);
      });

      const pompaByIrs = new Map();
      pompaList.forEach((p) => {
        const irs = normKey(p.irsaliyeNo);
        if (!irs) return;
        if (!pompaByIrs.has(irs)) pompaByIrs.set(irs, p);
      });

      // union keys
      const keys = new Set([
        ...Array.from(zoneByIrs.keys()),
        ...Array.from(mikserByIrs.keys()),
        ...Array.from(pompaByIrs.keys()),
      ]);

      const out = [];
      keys.forEach((irs) => {
        const z = zoneByIrs.get(irs) || null;
        const m = mikserByIrs.get(irs) || null;
        const p = pompaByIrs.get(irs) || null;

        const types = [];
        if (z) types.push("Zone Raporu");
        if (m) types.push("Mikser Sevkiyat");
        if (p) types.push("Pompa Sevkiyat");
        const recordType = types.join(" + ") || "";

        // temel alanlar: öncelik zone > mikser > pompa
        const base = z || m || p || {};

        const tarih = base.tarihStr || "";
        const tesis = base.tesis || "";
        const irsaliyeNo = base.irsaliyeNo || "";

        const musteriAdi = pickFirst(base.musteriAdi, z?.musteriAdi, m?.musteriAdi, p?.musteriAdi);
        const santiyeAdi = pickFirst(base.santiyeAdi, z?.santiyeAdi, m?.santiyeAdi, p?.santiyeAdi);

        const mikserPlaka = pickFirst(m?.mikserPlaka, z?.mikserPlaka, p?.mikserPlaka);
        const pompaPlaka = pickFirst(p?.pompaPlaka, z?.pompaPlaka, m?.pompaPlaka);

        const teslimSekli = pickFirst(base.teslimSekli, z?.teslimSekli, m?.teslimSekli, p?.teslimSekli);

        // 3 ayrı m3 + zone voto
        const uretimM3_zone = toNum(z?.satisM3 ?? z?.netM3 ?? z?.uretimM3 ?? z?.m3 ?? null);
        const votoKm = toNum(z?.votoKm ?? null);

        const m3_mikser = toNum(m?.fiiliM3 ?? m?.m3 ?? null);
        const m3_pompa = toNum(p?.fiiliM3 ?? p?.m3 ?? null);

        // Pompa saatleri (KESİNLİKLE sadece pompa)
        const pompaBasSaat = pickFirst(p?.baslangicSaat, "");
        const pompaBitSaat = pickFirst(p?.bitisSaat, "");
        const pompaSure = calcSureFromTimes(pompaBasSaat, pompaBitSaat);

        // Mikser saatleri (KESİNLİKLE sadece mikser)
        const mikserCikisSaat = pickFirst(m?.cikisSaat, "");
        const mikserDonusSaat = pickFirst(m?.donusSaat, "");
        const mikserSure = calcSureFromTimes(mikserCikisSaat, mikserDonusSaat);

        // Pompa personel (sadece pompa)
        const operator = collectIndexedNames(p, "operatorAdi");
        const yardimci = collectIndexedNames(p, "yagciAdi");

        // Mikser şoför ve KM (sadece mikser)
        const soforAdi = pickFirst(m?.soforAdi, "");
        const cikisKm = m?.cikisKm ?? "";
        const donusKm = m?.donusKm ?? "";
        const fiiliKm = calcFiiliKm(cikisKm, donusKm);
        const farkKm = calcFarkKm(votoKm, fiiliKm);

        // firma adları
        const mikserFirmaId = pickFirst(m?.mikserFirmaId, z?.mikserFirmaId, base.mikserFirmaId, "");
        const pompaFirmaId = pickFirst(p?.pompaFirmaId, z?.pompaFirmaId, base.pompaFirmaId, "");

        const mikserFirmasi = (() => {
          const key = String(mikserFirmaId ?? "");
          if (!key || key === "0") return key ? "0" : "";
          return FIRM_NAME_MAP[key] || key;
        })();

        const pompaFirmasi = (() => {
          const key = String(pompaFirmaId ?? "");
          if (!key || key === "0") return key ? "0" : "";
          return FIRM_NAME_MAP[key] || key;
        })();

        // uyarılar
        let warn = "";
        if (p && teslimSekli && teslimSekli !== "Pompalı") {
          warn = `UYARI: Pompa kaydı var ama teslimŞekli "${teslimSekli}"`;
        }

        out.push({
          _id: `irs:${irs}`,
          recordType,
          tarih,
          tesis,
          irsaliyeNo,
          musteriAdi,
          santiyeAdi,

          mikserPlaka,
          pompaPlaka,

          uretimM3_zone,
          votoKm,

          m3_mikser,
          mikserCikisSaat,
          mikserDonusSaat,
          mikserSure,
          soforAdi,
          cikisKm,
          donusKm,
          fiiliKm,
          farkKm,

          m3_pompa,
          pompaBasSaat,
          pompaBitSaat,
          pompaSure,
          operator,
          yardimci,

          mikserFirmasi,
          pompaFirmasi,
          teslimSekli,

          warn,
        });
      });

      out.sort((a, b) => {
        const d = String(a.tarih || "").localeCompare(String(b.tarih || ""));
        if (d) return d;
        const t = String(a.tesis || "").localeCompare(String(b.tesis || ""), "tr");
        if (t) return t;
        return String(a.irsaliyeNo || "").localeCompare(String(b.irsaliyeNo || ""));
      });

      setRows(out.filter((r) => r.tarih));

      // İlk yüklemede: boş olan/hiç veri gelmeyen kolonları otomatik gizle (Alanlar listesinde yine var)
   /*   if (firstLoadRef.current) {
        const baseRows = out.filter((r) => r.tarih);
        //const mustShow = new Set(["recordType","tarih","tesis","irsaliyeNo","musteriAdi","santiyeAdi","mikserPlaka","pompaPlaka","uretimM3_zone","votoKm","m3_mikser","m3_pompa"]);
        const mustShow = new Set(["uretimM3_zone","votoKm","m3_mikser","m3_pompa"]);
        const colsWithData = Object.keys(COLUMNS).filter((col) => {
          if (mustShow.has(col)) return true;
          return baseRows.some((r) => {
            const v = r[col];
            if (v === null || v === undefined) return false;
            if (typeof v === "number") return v !== 0;
            const s = String(v).trim();
            return s !== "";
          });
        });
        setVisibleCols(colsWithData);
        firstLoadRef.current = false;
      }
    } finally {
      setLoading(false);
    }
      */
     // İlk yüklemede: Sadece benim seçtiğim kolonları göster
      if (firstLoadRef.current) {
        // BURAYA SADECE EKRANDA İLK AÇILIŞTA GÖRMEK İSTEDİĞİN ANAHTARLARI YAZ
        const varsayilanSutunlar = [
          "tesis",
          "mikserPlaka",
          "m3_mikser",
          "pompaPlaka",
          "m3_pompa"
        ];
        
        // Sistemdeki tüm mantığı devre dışı bırakıp direkt senin listeni basıyoruz
        setVisibleCols(varsayilanSutunlar);
        
        firstLoadRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, FIRM_NAME_MAP]);

  // Tarih değişince otomatik getir
  useEffect(() => {
    const t = setTimeout(() => fetchReport(), 250);
    return () => clearTimeout(t);
  }, [fetchReport, startDate, endDate]);

  /* ======================= FİLTRELİ ======================= */
  const filtered = useMemo(() => {
    const base = rows.filter((r) => {
      if (startDate && r.tarih < startDate) return false;
      if (endDate && r.tarih > endDate) return false;
      return Object.entries(filters).every(([k, arr]) => {
        if (!arr?.length) return true;
        return arr.some((v) => norm(r[k]) === norm(v));
      });
    });
    return base;
  }, [rows, filters, startDate, endDate]);

const groupedRows = useMemo(() => {
    if (!groupBy) return { isGrouped: false };

    const groups = {};
    filtered.forEach((r) => {
      const key = r[groupBy] || "(Boş)";
      if (!groups[key]) {
        const initialTotals = {};
        // AGGREGATE_COLS içindeki her şeyi 0 ile başlatıyoruz (KM, m3, Süre vb.)
        AGGREGATE_COLS.forEach(col => initialTotals[col] = 0);
        groups[key] = { items: [], ...initialTotals };
      }
      groups[key].items.push(r);
      
      // Tüm sayısal alanları o grup için döngüyle topla
      AGGREGATE_COLS.forEach(col => {
        const val = parseFloat(r[col]) || 0;
        groups[key][col] += val;
      });
    });

    return { isGrouped: true, groups };
  }, [filtered, groupBy]);
  /* ======================= TOPLAM ======================= */
  const totals = useMemo(() => {
    const t = {};
    Object.keys(COLUMNS).forEach((k) => (t[k] = ""));
    NUMERIC.forEach((k) => {
      t[k] = filtered.reduce((a, r) => a + (toNum(r[k]) || 0), 0);
    });
    t.recordType = "TOPLAM";
    return t;
  }, [filtered]);

  /* ======================= FİLTRE OKU ======================= */
  const FilterArrow = ({ active, onClick }) => (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        marginLeft: 4,
        borderRadius: 3,
        border: `1px solid ${active ? "#93c5fd" : "#cbd5e1"}`,
        background: active ? "#dbeafe" : "#f1f5f9",
        color: active ? "#2563eb" : "#475569",
        fontSize: 10,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      ▾
    </span>
  );

/* ======================= FİLTRE DROPDOWN ======================= */
  const renderFilterDropdown = (col) => {
    const base = rows.filter((r) => {
      if (startDate && r.tarih < startDate) return false;
      if (endDate && r.tarih > endDate) return false;
      return Object.entries(filters).every(([k, arr]) => {
        if (k === col) return true;
        if (!arr?.length) return true;
        return arr.some((v) => norm(r[k]) === norm(v));
      });
    });

    let values = [...new Set(base.map((r) => r[col]).filter((v) => v !== undefined && v !== null))];
    values = values.map((v) => String(v)).filter((v) => v !== "");
    values.sort((a, b) => a.localeCompare(b, "tr"));
    if (filterSearch) values = values.filter((v) => norm(v).includes(norm(filterSearch)));

    const selected = filters[col] || [];
    const allSelected = values.length > 0 && selected.length === values.length;

    return ReactDOM.createPortal(
      <div
        ref={filterRef}
        style={{
          position: "fixed",
          top: filterPos.top,
          left: filterPos.left,
          width: filterPos.width,
          background: "#fff",
          border: "1px solid #93c5fd",
          padding: 6,
          zIndex: 9999,
          maxHeight: 260,
          overflowY: "auto",
          fontSize: 12,
          boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
        }}
      >
        <input
          placeholder="Ara..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          style={{ width: "100%", marginBottom: 6 }}
        />

        <label style={{ fontWeight: "bold", display: "block" }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => setFilters((p) => ({ ...p, [col]: e.target.checked ? values : [] }))}
          />{" "}
          Tümünü Seç
        </label>

        <hr />

        {values.map((v) => (
          <label key={v} style={{ display: "block", padding: "2px 0", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={selected.some((x) => norm(x) === norm(v))}
              onChange={() =>
                setFilters((p) => {
                  const cur = p[col] || [];
                  const exists = cur.some((x) => norm(x) === norm(v));
                  return { ...p, [col]: exists ? cur.filter((x) => norm(x) !== norm(v)) : [...cur, v] };
                })
              }
            />{" "}
            {/* BURASI DEĞİŞTİ: Eğer kolon tarih ise formatla göster */}
            {col === "tarih" ? dayjs(v).format("DD.MM.YYYY") : v}
          </label>
        ))}
      </div>,
      document.body
    );
  };

  /* ======================= EXCEL ======================= */
  const exportExcel = () => {
    const out = filtered.map((r) => {
      const o = {};
      safeVisibleCols.forEach((c) => {
        let v = r[c];
        if (NUMERIC.includes(c)) v = toNum(v) ?? v;
        o[COLUMNS[c]] = v ?? "";
      });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HazirBetonRapor");
    XLSX.writeFile(wb, `hazir_beton_rapor_${startDate}_${endDate}.xlsx`);
  };

  /* ======================= UI (TASARIM AYNI) ======================= */
  return (
    <div style={{ padding: 10, height: "calc(100vh - 40px)", display: "flex", flexDirection: "column" }}>
      {/* ÜST ALAN */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 8 }}>
        {/* SOL: ALANLAR */}
        <div style={{ width: 240 }}>
          <div style={{ border: "1px solid #cbd5e1", padding: 6, maxHeight: 210, overflowY: "auto", fontSize: 12 }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>Alanlar:</div>

            {allCols.map((col) => {
              const active = (filters[col]?.length || 0) > 0;
              return (
                <div key={col} style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={visibleCols.includes(col)}
                    onChange={() =>
                      setVisibleCols((p) => (p.includes(col) ? p.filter((c) => c !== col) : [...p, col]))
                    }
                  />
                  <span style={{ marginLeft: 6, flex: 1 }}>{COLUMNS[col]}</span>
                  <FilterArrow
                    active={active}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setFilterPos({ top: r.bottom + 4, left: r.left, width: 260 });
                      setOpenFilter(col);
                      setFilterSearch("");
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* ORTA: TARİH */}
<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          {/* ÜST SATIR: TARİH SEÇİMİ */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
            <b>Tarih:</b>
            <select value={preset} onChange={(e) => applyPreset(e.target.value)} style={{ padding: "2px 4px" }}>
              {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: "2px 4px" }} />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ padding: "2px 4px" }} />
          </div>

          {/* ALT SATIR: TASARIM PANELİ */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: "bold" }}>Tasarım:</span>
            <select 
              value={selectedDesignId} 
              onChange={(e) => applyDesign(e.target.value)}
              style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: 12, minWidth: 200, background: "#f8fafc" }}
            >
              <option value="">-- Kayıtlı Tasarım Seç --</option>
              {designs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            
            <input 
              type="text" 
              placeholder="Yeni Tasarım Adı..." 
              value={reportName} 
              onChange={e => setReportName(e.target.value)}
              style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: 12, width: 180 }}
            />
            
            <button 
              onClick={handleSaveDesign}
              style={{ background: "#2563eb", color: "white", padding: "5px 15px", borderRadius: "6px", cursor: "pointer", border: "none", fontSize: 12, fontWeight: "500" }}
            >
              Kaydet
            </button>
          </div>
        </div>

        {/* SAĞ: EXCEL */}
        <div>
          <button
            onClick={exportExcel}
            style={{
              background: "#22c55e",
              color: "#fff",
              padding: "6px 10px",
              border: "none",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Excel'e Aktar
          </button>
        </div>
      </div>

      {/* TABLO - İÇ SCROLL (ÇARŞAF GİBİ) */}
      <div style={{ borderSpacing: 0, overflow: "auto", position:"relative", border: "1px solid #e2e8f0", height: "calc(100vh - 220px)" }}>
        <table style={{ borderCollapse: "collapse", width: "max-content", minWidth: "100%", fontSize: 12 }}>

<thead style={{ background: "#f8fafc" }}>
  <tr style={{ background: "#f8fafc" }}>
    {safeVisibleCols.map((col) => {
      const active = (filters[col]?.length || 0) > 0;
      const isGrouped = groupBy === col;

      return (
        <th
          key={col}
          style={{
            border: "1px solid #cbd5e1",
            padding: "4px 8px",
            whiteSpace: "nowrap",
            background: isGrouped ? "#dbeafe" : "#f8fafc",
            position: "sticky",
            top: 0,
            zIndex: 3,
            cursor: "pointer",
            maxWidth: "200px", // Sütunların aşırı büyümesini engeller
            overflow: "hidden"
          }}
          onClick={() => setGroupBy(prev => (prev === col ? "" : col))}
        >
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            gap: "4px"
          }}>
            <span style={{ 
              fontWeight: isGrouped ? "bold" : "600",
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {COLUMNS[col]?.label || COLUMNS[col] || col}
              {isGrouped && " 📊"}
            </span>
            
            {/* Filtre Oku: Sabit genişlik verdik ki kaymasın */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                const r = e.currentTarget.getBoundingClientRect();
                setFilterPos({ top: r.bottom + 4, left: r.left, width: 260 });
                setOpenFilter(col);
                setFilterSearch("");
              }}
              style={{ minWidth: "20px", display: "flex", justifyContent: "center" }}
            >
              <FilterArrow active={active} />
            </div>
          </div>
        </th>
      );
    })}
  </tr>
  {/* Genel Toplam Satırı aynı kalabilir... */}
</thead>
<tbody>
  {!groupedRows.isGrouped ? (
    filtered.map((r) => (
      <tr key={r._id} style={{ background: "white" }}>
        {safeVisibleCols.map((c) => (
          <td key={c} style={{ border: "1px solid #cbd5e1", padding: "6px", fontSize: "11px" }}>
            {/* Tarih sütununu formatlıyoruz, diğerlerini olduğu gibi basıyoruz */}
            {c === "tarih" && r[c] 
              ? dayjs(r[c]).format("DD.MM.YYYY") 
              : (r[c] ?? "")}
          </td>
        ))}
      </tr>
    ))
  ) : (
    Object.entries(groupedRows.groups).map(([groupName, gData]) => (
      <React.Fragment key={groupName}>
        {/* GRUP TOPLAM SATIRI */}
        <tr style={{ background: "#f8fafc", fontWeight: "bold" }}>
          {safeVisibleCols.map((col) => {
            if (col === groupBy) {
              return (
                <td key={col} style={{ border: "1px solid #cbd5e1", padding: "8px", color: "#1e40af" }}>
                  📊 {groupName || "(Boş)"} ({gData.items.length} Sefer)
                </td>
              );
            }
            if (AGGREGATE_COLS.includes(col)) {
              const value = gData[col] || 0;
              return (
                <td key={col} style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "right", color: "#059669" }}>
                  {value % 1 === 0 ? value : value.toFixed(2)}
                </td>
              );
            }
            return <td key={col} style={{ border: "1px solid #cbd5e1" }}></td>;
          })}
        </tr>
        
        {/* GRUBA AİT DETAY SATIRLARI (Tarih burada da formatlandı) */}
        {gData.items.map((r) => (
          <tr key={r._id} style={{ background: "white" }}>
            {safeVisibleCols.map((c) => (
              <td key={c} style={{ border: "1px solid #cbd5e1", padding: "4px 8px", fontSize: "11px", color: "#64748b" }}>
                {c === "tarih" && r[c] 
                  ? dayjs(r[c]).format("DD.MM.YYYY") 
                  : (r[c] ?? "")}
              </td>
            ))}
          </tr>
        ))}
      </React.Fragment>
    ))
  )}
</tbody>
        </table>
      </div>

      {openFilter && renderFilterDropdown(openFilter)}
    </div>
  );
}
